import {
  APP_API_TOKEN,
  getTokenKey,
  setRuntimeAppId,
} from '@common/constants/shared';
import auth, {
  AuthenticationClient,
  AuthenticationClientOptions,
  Storage,
} from '@feathersjs/authentication-client';
import {
  AuthenticationRequest,
  AuthenticationResult,
} from '@feathersjs/authentication/lib';

import feathers, { Application } from '@feathersjs/feathers';
import socketio from '@feathersjs/socketio-client';
import AsyncStorage from '@react-native-async-storage/async-storage';
import io from 'socket.io-client';
import appConfig from '../../../appConfig.json';
import { Platform } from 'react-native';
import { API_ENDPOINTS } from '@common/constants/apiEndpoint';
import { logSocketDiagnostic } from './crashLogger';

let appId: string;
if (Platform.OS === 'web') {
  // find appId from pathname
  appId = window.location.pathname
    .split('/')
    .filter(
      (path) =>
        path &&
        !['preview', 'share-modal', 'share-app', 'preview-v4'].includes(path)
    )[0];
  // Publish appId so getTokenKey() can namespace localStorage per-tab.
  // sessionStorage is per-tab, isolating siblings on the same origin.
  setRuntimeAppId(appId);
} else {
  appId = appConfig.appId;
}

export const socket = io(appConfig.baseUrl, {
  query: { appId: appId },
  // Polling-first (socket.io default): connect reliably over HTTP/XHR — which
  // works on IPv6-only carriers (Rakuten 5G) via NAT64 — then auto-upgrade to
  // websocket when the network allows. socket.io only upgrades polling ->
  // websocket (never the reverse), so this ordering is resilient without any
  // manual fallback.
  transports: ['polling', 'websocket'],
  // Connection timeout before connect_error fires (socket.io then reconnects).
  timeout: 10000,
});

// Surface WHY the socket fails to connect. On native these events are forwarded
// to Firebase Crashlytics (crashLogger.native.ts); on web it is a no-op.
const describeTransport = (): string => {
  try {
    return (socket as any)?.io?.engine?.transport?.name ?? 'n/a';
  } catch {
    return 'n/a';
  }
};
const logSocket = (event: string, error?: unknown): void => {
  const message = `[socket.io] ${event} (transport=${describeTransport()})`;
  // Use console.error: only console.error is captured in the iOS log pipeline.
  // eslint-disable-next-line no-console
  console.error(message, error ?? '');
  logSocketDiagnostic(message, error, appId);
};
socket.on('connect', () => logSocket('connect'));
socket.on('connect_error', (err: unknown) => logSocket('connect_error', err));
socket.on('connect_timeout', () => logSocket('connect_timeout'));
socket.on('error', (err: unknown) => logSocket('error', err));
socket.on('reconnect_failed', () => logSocket('reconnect_failed'));
socket.on('disconnect', (reason: unknown) => logSocket('disconnect', reason));

const app = feathers();

class MyAuthenticationClient extends AuthenticationClient {
  appId: string;
  constructor(app: Application, options: AuthenticationClientOptions) {
    super(app, options);

    if (Platform.OS === 'web') {
      this.appId = window.location.pathname
        .split('/')
        .filter((path) => path && path !== 'preview')[0];
    } else {
      this.appId = appConfig.appId;
    }
  }
  get storage(): Storage {
    return {
      getItem: (key: string) =>
        AsyncStorage.getItem && AsyncStorage.getItem(key),
      setItem: (key: string, value: any) => {
        AsyncStorage.setItem && AsyncStorage.setItem(key, value ?? '');
      },
      removeItem: (key: string) =>
        AsyncStorage.removeItem && AsyncStorage.removeItem(key),
    };
  }

  async getAccessToken() {
    const token = await this.storage.getItem(getTokenKey(this.appId));
    return token || APP_API_TOKEN;
  }

  async setAccessToken(accessToken: string) {
    return (
      this.storage.setItem &&
      this.storage.setItem(
        getTokenKey(this.appId),
        accessToken || APP_API_TOKEN
      )
    );
  }

  async authenticate(
    authentication?: AuthenticationRequest,
    params?: feathers.Params
  ): Promise<AuthenticationResult> {
    const newParams = {
      ...params,
      query: { appId },
    };

    return super.authenticate(authentication, newParams);
  }

  async logout(): Promise<AuthenticationResult | null> {
    return this.authenticate({ strategy: 'jwt', accessToken: APP_API_TOKEN });
  }

  async reAuthenticate(
    force?: boolean | undefined,
    strategy?: string | undefined
  ): Promise<AuthenticationResult> {
    return super.reAuthenticate(force, strategy);
  }
}

// Setup the transport (Rest, Socket, etc.) here
app.configure(
  socketio(socket, {
    // Per service-call ack timeout: how long to wait for the server to respond
    // to a service method call once the socket is connected.
    timeout: 30000,
  })
);

// Pass the custom authentication client class as the `Authentication` option
app.configure(
  auth({
    Authentication: MyAuthenticationClient,
    storage: AsyncStorage,
    storageKey: getTokenKey(appId),
    path: API_ENDPOINTS.SIGNIN,
  })
);

app.hooks({
  error(context) {
    const { error } = context;

    if (error.code === 401) {
      app.emit('authFailure', error);
    }

    return context;
  },
});

export default app;
